import {
  compareSeq,
  LIMITS,
  type ConversationSummary,
  type FileInfo,
  type LinkPreview,
  type Message,
  type Reaction,
  type Receipt,
  type ReplyRef,
  type Seq,
  type SystemEvent,
} from '@sd/contracts';
import type { ClientState, PendingMessage, SendState } from './state';

/** Everything the timeline renderer needs for one bubble, for both confirmed and pending messages. */
export interface MessageView {
  /** Stable React key: server id, or the clientMessageId while pending. */
  key: string;
  id: string | null;
  seq: Seq | null;
  clientMessageId: string | null;
  senderId: string;
  mine: boolean;
  text: string | null;
  deleted: boolean;
  edited: boolean;
  createdAt: string;
  revision: number;
  reply: ReplyRef | null;
  attachments: FileInfo[];
  mentionsMe: boolean;
  /** Own messages only. */
  state: SendState | null;
  receipt: Receipt | null;
  /** As the chips show them: the server's summary with the viewer's unanswered changes applied. */
  reactions: Reaction[];
  upload: PendingMessage['upload'];
  lastError: string | null;
  /** Cards for links in the text, once the server fetched them. */
  linkPreviews: LinkPreview[];
  /** Sent with "전달" from another conversation. */
  forwarded: boolean;
}

export type GroupPosition = 'single' | 'first' | 'mid' | 'last';

/** The viewer's reaction changes the server has not answered yet, applied to its summary so a click shows at once. */
export function withPendingReactions(reactions: readonly Reaction[], pending: Readonly<Record<string, boolean>> | undefined, meId: string): Reaction[] {
  if (!pending) return reactions as Reaction[];
  const out = [...reactions];
  for (const [emoji, on] of Object.entries(pending)) {
    const index = out.findIndex((r) => r.emoji === emoji);
    const current = out[index];
    if (Boolean(current?.me) === on) continue;
    if (!current) out.push({ emoji, count: 1, me: true, userIds: [meId] });
    else if (on) out[index] = { ...current, count: current.count + 1, me: true, userIds: [meId, ...current.userIds].slice(0, LIMITS.reactionPreviewUsers) };
    else if (current.count > 1) out[index] = { ...current, count: current.count - 1, me: false, userIds: current.userIds.filter((id) => id !== meId) };
    else out.splice(index, 1);
  }
  return out;
}

export type TimelineItem =
  | { kind: 'date'; key: string; at: string }
  | { kind: 'system'; key: string; messageId: string; event: SystemEvent; at: string }
  | { kind: 'unread'; key: 'unread' }
  | { kind: 'message'; key: string; message: MessageView; position: GroupPosition; showName: boolean; showAvatar: boolean; showTime: boolean };

/** Local calendar day of an ISO timestamp (the viewer's time zone). */
export function localDayKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${d.getMonth() + 1}-${d.getDate()}`;
}

/** Send state of a confirmed own message: read once anyone eligible has read it. */
export function confirmedState(message: Message): SendState {
  return message.receipt && message.receipt.readCount > 0 ? 'read' : 'sent';
}

/**
 * Own messages keep the key they had while pending (their clientMessageId), so the row is not rebuilt when the
 * server confirms it. Only one's own: another sender's clientMessageId is theirs to choose.
 */
const messageKey = (message: Message, mine: boolean) => (mine && message.clientMessageId ? message.clientMessageId : message.id);

export function viewOfMessage(message: Message, meId: string, pendingReactions?: Readonly<Record<string, boolean>>): MessageView {
  const mine = message.senderId === meId;
  return {
    key: messageKey(message, mine),
    id: message.id,
    seq: message.seq,
    clientMessageId: message.clientMessageId,
    senderId: message.senderId ?? '',
    mine,
    text: message.deletedAt ? null : message.text,
    deleted: Boolean(message.deletedAt),
    edited: Boolean(message.editedAt) && !message.deletedAt,
    createdAt: message.createdAt,
    revision: message.revision,
    reply: message.deletedAt ? null : message.replyTo,
    attachments: message.deletedAt ? [] : message.attachments,
    mentionsMe: !mine && message.mentions.includes(meId),
    state: mine && message.kind === 'text' ? confirmedState(message) : null,
    receipt: mine ? message.receipt : null,
    reactions: message.deletedAt ? [] : withPendingReactions(message.reactions ?? [], pendingReactions, meId),
    upload: null,
    lastError: null,
    linkPreviews: message.deletedAt ? [] : (message.linkPreviews ?? []),
    forwarded: Boolean(message.forwarded) && !message.deletedAt,
  };
}

export function viewOfPending(pending: PendingMessage, meId: string): MessageView {
  return {
    key: pending.clientMessageId,
    id: null,
    seq: null,
    clientMessageId: pending.clientMessageId,
    senderId: meId,
    mine: true,
    text: pending.text || null,
    deleted: false,
    edited: false,
    createdAt: pending.createdAt,
    revision: 0,
    reply: null,
    attachments: pending.attachments,
    mentionsMe: false,
    state: pending.state,
    receipt: null,
    reactions: [],
    upload: pending.upload,
    lastError: pending.lastError,
    linkPreviews: [],
    forwarded: false,
  };
}

export interface BuildTimelineInput {
  messages: readonly Message[];
  pending: readonly PendingMessage[];
  meId: string;
  kind: ConversationSummary['kind'];
  /** Seq of the first unread message from others when the conversation was opened. */
  unreadAnchorSeq: Seq | null;
  /** Reply excerpts for pending messages, looked up from the loaded timeline. */
  replyFor?: (messageId: string) => ReplyRef | null;
  pendingReactions?: ClientState['pendingReactions'];
}

/**
 * Turns confirmed + pending messages into renderable rows: day dividers,
 * system lines, the unread divider and bubble grouping. Dividers and system
 * lines break bubble groups, like the design.
 */
export function buildTimeline(input: BuildTimelineInput): TimelineItem[] {
  const out: TimelineItem[] = [];
  let day: string | null = null;
  let unreadPlaced = false;

  const pushDay = (iso: string) => {
    const key = localDayKey(iso);
    if (key !== day) {
      day = key;
      out.push({ kind: 'date', key: `date-${key}`, at: iso });
    }
  };

  for (const message of input.messages) {
    pushDay(message.createdAt);
    if (
      !unreadPlaced &&
      input.unreadAnchorSeq !== null &&
      message.senderId !== input.meId &&
      compareSeq(message.seq, input.unreadAnchorSeq) >= 0
    ) {
      out.push({ kind: 'unread', key: 'unread' });
      unreadPlaced = true;
    }
    if ((message.kind === 'system' && message.system) || message.kind === 'unsupported') {
      // A message kind added by a newer server shows as a system line asking for an update.
      out.push({ kind: 'system', key: message.id, messageId: message.id, event: message.system ?? { type: 'unsupported' }, at: message.createdAt });
      continue;
    }
    const view = viewOfMessage(message, input.meId, input.pendingReactions?.[message.id]);
    out.push({ kind: 'message', key: view.key, message: view, position: 'single', showName: false, showAvatar: false, showTime: false });
  }

  // A send the server has confirmed shows once, as the confirmed message (it holds the same key).
  const confirmed = new Set(input.messages.flatMap((message) => (message.senderId === input.meId && message.clientMessageId ? [message.clientMessageId] : [])));
  const pending = [...input.pending].filter((item) => !confirmed.has(item.clientMessageId)).sort((a, b) => a.createdAt.localeCompare(b.createdAt));
  for (const item of pending) {
    pushDay(item.createdAt);
    const view = viewOfPending(item, input.meId);
    if (item.replyToMessageId) view.reply = input.replyFor?.(item.replyToMessageId) ?? null;
    out.push({ kind: 'message', key: item.clientMessageId, message: view, position: 'single', showName: false, showAvatar: false, showTime: false });
  }

  // Grouping pass: consecutive bubbles from the same sender.
  for (let i = 0; i < out.length; i += 1) {
    const item = out[i]!;
    if (item.kind !== 'message') continue;
    const prev = out[i - 1];
    const next = out[i + 1];
    const sameAsPrev = prev?.kind === 'message' && prev.message.senderId === item.message.senderId;
    const sameAsNext = next?.kind === 'message' && next.message.senderId === item.message.senderId;
    item.position = !sameAsPrev && !sameAsNext ? 'single' : !sameAsPrev ? 'first' : !sameAsNext ? 'last' : 'mid';
    item.showName = !item.message.mine && !sameAsPrev && input.kind === 'group';
    item.showAvatar = !item.message.mine && !sameAsPrev;
    item.showTime = item.message.mine || !sameAsNext;
  }
  return out;
}

/** Conversation list order: most recent activity first (pinned ones are shown separately by the UI). */
export function sortConversations(conversations: readonly ConversationSummary[]): ConversationSummary[] {
  return [...conversations].sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt) || a.id.localeCompare(b.id));
}

/** Count of unread messages from others after the anchor (for "새 메시지 N개"). */
export function unreadAfterAnchor(messages: readonly Message[], anchor: Seq | null, meId: string): number {
  if (anchor === null) return 0;
  return messages.filter((m) => m.kind === 'text' && m.senderId !== meId && compareSeq(m.seq, anchor) >= 0).length;
}
